import Config from '@/config'
import { FRAME_ID, createDebug } from '@/utils/debug'

const log = createDebug('ppt:frame-badge')

// AHA-43111: paints this frame's FRAME_ID onto its own surface.
//
// PowerPoint on the web boots one add-in frame per slide, all at once and all from the same
// manifest URL, and each is an independent JS context with its own router. The console shows
// every frame's output interleaved and tagged `[frame xxxxxx]`, but nothing on screen says WHICH
// of them you are actually looking at — so a log line reading `routed -> PresentationDetail` and
// a viewport still showing the presentation list look contradictory when they are simply two
// different frames. Rendering the id into the frame itself makes that correspondence directly
// observable: read the id off the screen, then follow that id through the console.
//
// It carries the per-frame state that goes with it — the path, whether the browser considers this
// frame focused, and (AHA-43274) the add-in view and stand-down flag. All of it is state each frame
// holds its OWN copy of, which is the property that makes it worth painting per-frame rather than
// logging once.
//
// Debug-only, behind TWO gates that must both be open. The badge draws on top of the add-in's own
// surface, which in the Slide Show is what an audience is looking at, so a single accidental
// `localStorage.debug` must not be able to put it there:
//   1. build-level — VUE_APP_ENABLE_ADD_IN_POWERPOINT_DEBUG=true (absent from every shipped .env)
//   2. runtime — localStorage.debug = 'aha:ppt:*'  (or 'aha:ppt:frame-badge' for just this)
const BADGE_ELEMENT_ID = 'aha-ppt-frame-badge'

// The path is re-read rather than pushed from the router: routing here happens through several
// mechanisms (router.replace, full-page navigation, Office reloading the frame), and a poll
// observes all of them without this module having to know about any.
const REFRESH_MS = 500

// AHA-43274: the two pieces of state the auto-stop flow turns on, read off the frame instead of
// reconstructed from the console.
//
// `addinPPTActiveView` is which view this frame last committed to (edit vs read), and
// `pptSlideLostFocus` is whether the tag poll has stood this frame down from the show. Together
// they are two of the four terms behind the isPptFrameStoodDown getter, and the two that actually
// move during a slideshow — the other two (the add-in build, the Unleash flag) are fixed for the
// session. On the web every frame in the deck holds its own copy of both, which is exactly why
// reading them per-frame beats reading them in a shared console.
//
// LAZILY required, not imported at module scope. This module is pulled in from the Office boot
// path, which the store's own module graph reaches back into, so a static import would close a
// cycle and leave the badge undefined at boot — the same hazard debug.js documents for
// ppt-online.utils, handled the same way. By the time the poll first fires, every module is
// evaluated.
const readAddinState = () => {
  try {
    // eslint-disable-next-line global-require
    const { state } = require('@/vuex/store').default
    if (!state) return ''
    return ` ${state.addinPPTActiveView} lost:${state.pptSlideLostFocus ? 'Y' : 'n'}`
  } catch (error) {
    // A badge that cannot read the store still has an id and a path worth showing.
    return ''
  }
}

const BADGE_STYLE = [
  'position:fixed',
  'top:0',
  'left:0',
  'z-index:2147483647',
  'padding:2px 6px',
  'font:11px/1.4 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
  'color:#fff',
  'background:rgba(0,0,0,.72)',
  'border-bottom-right-radius:4px',
  'white-space:nowrap',
  'max-width:100vw',
  'overflow:hidden',
  'text-overflow:ellipsis',
  // Must never intercept a click: this sits on top of the add-in's own UI, and in the Slide Show
  // that UI is what the audience is interacting with.
  'pointer-events:none',
  'user-select:none',
].join(';')

const mountBadge = () => {
  const badge = document.createElement('div')
  badge.id = BADGE_ELEMENT_ID
  badge.setAttribute('style', BADGE_STYLE)

  const render = () => {
    // pathname + search only: the origin is identical in every frame and would push the part that
    // actually differs off the edge of a narrow slide-show frame.
    //
    // hasFocus is per-document, so it reports on THIS iframe: with several frames on screen it
    // shows at a glance which one the browser considers focused, without correlating ids in the
    // console. The badge is repainted on a poll, so it tracks focus moving between frames live.
    //
    // The add-in state goes BEFORE the path deliberately: the path is the part that overflows a
    // narrow slide-show frame and gets ellipsised away, and it is the least interesting of the
    // three once a show is running.
    const focused = document.hasFocus() ? 'FOCUS' : '     '
    badge.textContent = `${FRAME_ID} ${focused}${readAddinState()} ${window.location.pathname}${window.location.search}`
    badge.style.background = document.hasFocus() ? 'rgba(26, 26, 26,.85)' : 'rgba(0,0,0,.72)'
  }
  render()

  const attach = () => {
    if (!document.body) return false
    document.body.appendChild(badge)
    return true
  }
  // The office boot path can run before <body> exists, so retry once on DOMContentLoaded.
  if (!attach()) {
    document.addEventListener('DOMContentLoaded', attach, { once: true })
  }

  setInterval(render, REFRESH_MS)
  log('frame badge mounted |', FRAME_ID)
}

export const mountFrameIdBadge = () => {
  if (typeof document === 'undefined') return
  if (!Config.isEnableAddinPowerpointDebug) return
  // `.log` is the underlying `debug` instance (createDebug preserves it); `.enabled` reflects the
  // namespace filter, so even a debug build stays clean until the namespace is turned on.
  if (!log.log || !log.log.enabled) return
  if (document.getElementById(BADGE_ELEMENT_ID)) return

  // A debug decoration must never be able to break the caller. This is mounted from the Office
  // boot path, so anything thrown here — a partial DOM, a stubbed document — would take the whole
  // add-in cold-load down with it, which is precisely the failure the badge exists to help debug.
  try {
    mountBadge()
  } catch (error) {
    log('frame badge failed to mount', error)
  }
}

export default mountFrameIdBadge
